(() => {
  if (window.__kittedPreorderUxInitialized) return;
  window.__kittedPreorderUxInitialized = true;

  const PREORDER_LABEL = 'Preorder now';
  const ADD_TO_CART_LABEL = 'Add to cart';
  const PREORDER_PROPERTY = 'properties[PREORDER]';
  const PREORDER_VALUE = 'YES — 2–3 weeks from the UK';
  const PREORDER_NOTICE = 'Sold out — available on order. Preorder shipping: 2–3 weeks from the UK.';

  const SIZE_PROMPT_LABEL = 'Select a size';
  const initializedPickers = new WeakSet();
  const selectedSizePickers = new WeakSet();

  const sizeControl = (target) => {
    if (!(target instanceof Element)) return null;
    if (target instanceof HTMLSelectElement) return target.selectedOptions?.[0] || null;
    if (target instanceof HTMLOptionElement) return target;
    if (target instanceof HTMLInputElement) return target;
    return target.closest('label')?.querySelector('input') || null;
  };

  const isSizeControl = (control) =>
    Boolean(control && (control.dataset.optionName || '').toLowerCase().includes('siz'));

  const pickerComponents = (picker) => {
    const scope = picker.closest('.shopify-section, dialog') || document;
    const productId = picker.dataset.productId;
    if (!productId) return Array.from(scope.querySelectorAll('product-form-component'));
    const escapedId = CSS.escape(productId);
    return Array.from(scope.querySelectorAll(`product-form-component[data-product-id="${escapedId}"]`));
  };

  const setSizeRequiredState = (picker, awaitingSize, control = null) => {
    picker.dataset.kittedAwaitingSize = awaitingSize ? 'true' : 'false';
    const unavailable = control?.dataset.optionAvailable === 'false';

    pickerComponents(picker).forEach((component) => {
      const form = component.querySelector('form[data-type="add-to-cart-form"], form[action*="/cart/add"]');
      const variantInput = form?.querySelector('input[name="id"]');
      const button = component.querySelector('button[ref="addToCartButton"]');
      const paymentButton = component.querySelector('.shopify-payment-button');

      if (variantInput) variantInput.disabled = awaitingSize;
      if (button) {
        button.disabled = awaitingSize || unavailable;
        if (awaitingSize) setButtonLabel(button, SIZE_PROMPT_LABEL);
      }
      if (paymentButton) paymentButton.hidden = awaitingSize || unavailable;
    });
  };

  const updateInventoryImmediately = (picker, control) => {
    if (!control) return;
    const scope = picker.closest('.shopify-section, dialog') || document;
    const productId = picker.dataset.productId;
    const inventory = productId
      ? scope.querySelector(`product-inventory[data-product-id="${CSS.escape(productId)}"]`)
      : scope.querySelector('product-inventory.product-inventory--kitted');
    if (!inventory) return;

    const quantity = Number.parseInt(control.dataset.inventoryQuantity || '0', 10);
    const threshold = Number.parseInt(inventory.dataset.inventoryThreshold || '5', 10);
    const managed = control.dataset.inventoryManagement === 'shopify';
    const policy = control.dataset.inventoryPolicy || '';
    const available = control.dataset.optionAvailable !== 'false';

    let status = 'out_of_stock';
    let strongText = 'Sold out';
    let detailText = ' in this size.';

    if (managed && quantity > 0) {
      if (quantity <= threshold) {
        status = 'low';
        strongText = `Only ${quantity} left in this size`;
        detailText = ' — order before it’s gone.';
      } else {
        status = 'in_stock';
        strongText = `${quantity} in stock`;
      }
    } else if (managed && policy === 'continue') {
      status = 'continue';
      strongText = 'Sold out — available on order.';
      detailText = ' Preorder shipping: 2–3 weeks from the UK.';
    } else if (!managed && available) {
      status = 'in_stock_untracked';
      strongText = 'In stock';
    }

    const statusElement = inventory.querySelector('.product-inventory__status');
    const textElement = inventory.querySelector('.product-inventory__text');
    if (!statusElement || !textElement) return;

    statusElement.classList.remove(
      'product-inventory__status--low',
      'product-inventory__status--in_stock',
      'product-inventory__status--continue',
      'product-inventory__status--in_stock_untracked',
      'product-inventory__status--out_of_stock'
    );
    statusElement.classList.add(`product-inventory__status--${status}`);

    const strong = document.createElement('strong');
    strong.textContent = strongText;
    const detail = document.createElement('span');
    detail.className = 'product-inventory__cta';
    detail.textContent = detailText;
    textElement.replaceChildren(strong, detail);

    inventory.dataset.stockStatus = status;
    inventory.dataset.sizeSelected = 'true';
  };

  const initializeRequiredSizePicker = (picker) => {
    if (!(picker instanceof HTMLElement) || picker.closest('product-card') || initializedPickers.has(picker)) return;
    const controls = Array.from(picker.querySelectorAll('fieldset input')).filter(isSizeControl);
    if (controls.length === 0) return;

    initializedPickers.add(picker);
    const explicitVariant = !picker.closest('dialog') && new URL(window.location.href).searchParams.has('variant');
    if (explicitVariant) {
      selectedSizePickers.add(picker);
      const selected = controls.find((control) => control.checked) || null;
      setSizeRequiredState(picker, false, selected);
      if (selected) updateInventoryImmediately(picker, selected);
      return;
    }

    controls.forEach((control) => {
      control.checked = false;
      control.removeAttribute('checked');
      control.dataset.currentChecked = 'false';
      control.dataset.previousChecked = 'false';
    });
    setSizeRequiredState(picker, true);
  };

  const handleSizeSelection = (picker, target) => {
    const control = sizeControl(target);
    if (!isSizeControl(control)) return false;
    selectedSizePickers.add(picker);
    setSizeRequiredState(picker, false, control);
    updateInventoryImmediately(picker, control);
    return true;
  };


  let pendingFitAction = null;

  const productPickerForForm = (form) => {
    const component = form.closest('product-form-component');
    if (!component) return null;
    const scope = component.closest('.shopify-section, dialog') || document;
    const productId = component.dataset.productId;
    const pickers = Array.from(scope.querySelectorAll('variant-picker'));
    const picker = pickers.find((candidate) => !productId || candidate.dataset.productId === productId) || null;
    return picker;
  };

  const returnToProduct = () => {
    pendingFitAction = null;
    const dialog = document.querySelector('[data-kitted-fit-dialog]');
    if (dialog instanceof HTMLDialogElement) dialog.close();
  };

  const continueToCart = async () => {
    const action = pendingFitAction;
    const dialog = document.querySelector('[data-kitted-fit-dialog]');
    const continueButton = dialog?.querySelector('[data-kitted-fit-continue]');
    if (!action?.form || !(continueButton instanceof HTMLButtonElement)) return;

    continueButton.disabled = true;
    continueButton.textContent = 'Adding…';

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(action.form),
      });
      if (!response.ok) throw new Error('Unable to add this size to the cart.');

      pendingFitAction = null;
      window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
    } catch (error) {
      continueButton.disabled = false;
      continueButton.textContent = 'Continue to cart';
      let errorMessage = dialog.querySelector('[data-kitted-fit-error]');
      if (!errorMessage) {
        errorMessage = document.createElement('p');
        errorMessage.setAttribute('data-kitted-fit-error', 'true');
        errorMessage.setAttribute('role', 'alert');
        errorMessage.className = 'kitted-fit-dialog__error';
        dialog.querySelector('.kitted-fit-dialog__actions')?.after(errorMessage);
      }
      errorMessage.textContent = error instanceof Error ? error.message : 'Unable to add this size to the cart.';
    }
  };

  const ensurePurchaseNoticeDialog = () => {
    let dialog = document.querySelector('[data-kitted-fit-dialog]');
    if (dialog instanceof HTMLDialogElement) return dialog;

    const style = document.createElement('style');
    style.textContent = `
      .kitted-fit-dialog {
        width: min(92vw, 520px);
        padding: 0;
        border: 1px solid rgb(212 175 55 / 0.65);
        border-radius: 20px;
        background: rgb(255 255 255 / 0.98);
        color: #151515;
        box-shadow: 0 24px 70px rgb(0 0 0 / 0.28);
      }
      .kitted-fit-dialog::backdrop {
        background: rgb(0 0 0 / 0.58);
        backdrop-filter: blur(5px);
      }
      .kitted-fit-dialog__content {
        padding: 30px;
        text-align: center;
      }
      .kitted-fit-dialog__eyebrow {
        margin: 0 0 10px;
        color: #9a7510;
        font-size: 0.76rem;
        font-weight: 800;
        letter-spacing: 0.14em;
        text-transform: uppercase;
      }
      .kitted-fit-dialog__title {
        margin: 0 0 12px;
        font-size: clamp(1.35rem, 4vw, 1.8rem);
        line-height: 1.15;
      }
      .kitted-fit-dialog__text {
        margin: 0 auto 24px;
        max-width: 430px;
        color: #4a4a4a;
        font-size: 1rem;
        line-height: 1.55;
      }
      .kitted-fit-dialog__actions {
        display: grid;
        gap: 10px;
      }
      .kitted-fit-dialog__button {
        width: 100%;
        min-height: 48px;
        padding: 12px 18px;
        border-radius: 999px;
        font: inherit;
        font-weight: 750;
        cursor: pointer;
      }
      .kitted-fit-dialog__button--continue {
        border: 1px solid #111;
        background: #111;
        color: #fff;
      }
      .kitted-fit-dialog__button--back {
        border: 1px solid #c9a227;
        background: #fff;
        color: #171717;
      }
      .kitted-fit-dialog__button:focus-visible {
        outline: 3px solid rgb(212 175 55 / 0.45);
        outline-offset: 3px;
      }
      @media screen and (min-width: 560px) {
        .kitted-fit-dialog__actions {
          grid-template-columns: 1fr 1fr;
        }
      }
    `;
    document.head.appendChild(style);

    dialog = document.createElement('dialog');
    dialog.className = 'kitted-fit-dialog';
    dialog.setAttribute('data-kitted-fit-dialog', 'true');
    dialog.setAttribute('aria-labelledby', 'KittedFitDialogTitle');
    dialog.innerHTML = `
      <div class="kitted-fit-dialog__content">
        <p class="kitted-fit-dialog__eyebrow" data-kitted-fit-eyebrow>Important notice</p>
        <h2 class="kitted-fit-dialog__title" id="KittedFitDialogTitle">Please note</h2>
        <p class="kitted-fit-dialog__text" data-kitted-fit-text></p>
        <div class="kitted-fit-dialog__actions">
          <button class="kitted-fit-dialog__button kitted-fit-dialog__button--continue" type="button" data-kitted-fit-continue>Continue to cart</button>
          <button class="kitted-fit-dialog__button kitted-fit-dialog__button--back" type="button" data-kitted-fit-back>Return to product</button>
        </div>
      </div>
    `;
    dialog.querySelector('[data-kitted-fit-continue]')?.addEventListener('click', continueToCart);
    dialog.querySelector('[data-kitted-fit-back]')?.addEventListener('click', returnToProduct);
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      returnToProduct();
    });
    document.body.appendChild(dialog);
    return dialog;
  };

  const interceptPurchaseClick = (event) => {
    const button = event.target instanceof Element
      ? event.target.closest('button[name="add"], button.shopify-payment-button__button, button[data-testid*="accelerated"], shopify-accelerated-checkout button')
      : null;
    if (!(button instanceof HTMLElement)) return;

    const isAddToCart = button.matches('button[name="add"]');
    const isBuyNow = /buy it now/i.test((button.textContent || '').trim());
    if (!isAddToCart && !isBuyNow) return;

    const form = button.closest('form');
    if (!(form instanceof HTMLFormElement)) return;
    const picker = productPickerForForm(form);
    if (!picker) return;
    const selectedSize = selectedControl(picker);
    if (!isSizeControl(selectedSize)) return;

    const playerEdition = (picker.dataset.productTitle || '').toLowerCase().includes('player edition');
    const preorder = isPreorderSelection(selectedSize);
    if (!playerEdition && !preorder) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    setPreorderProperty(form, preorder);
    pendingFitAction = { form, picker };
    const dialog = ensurePurchaseNoticeDialog();
    const eyebrow = dialog.querySelector('[data-kitted-fit-eyebrow]');
    const title = dialog.querySelector('#KittedFitDialogTitle');
    const message = dialog.querySelector('[data-kitted-fit-text]');
    if (eyebrow) eyebrow.textContent = preorder ? 'Preorder notice' : 'Player Edition fit notice';
    if (title) title.textContent = preorder ? 'You are preordering this item' : 'We recommend sizing up';
    if (message) {
      message.textContent = preorder
        ? playerEdition
          ? 'Please note you are preordering this item, which will take 2–3 weeks. We also recommend sizing up on this Player Edition kit.'
          : 'Please note you are preordering this item, which will take 2–3 weeks.'
        : 'This Player Edition kit has a slim, athletic fit. We recommend sizing up.';
    }
    const errorMessage = dialog.querySelector('[data-kitted-fit-error]');
    if (errorMessage) errorMessage.remove();
    const continueButton = dialog.querySelector('[data-kitted-fit-continue]');
    if (continueButton instanceof HTMLButtonElement) {
      continueButton.disabled = false;
      continueButton.textContent = 'Continue to cart';
    }
    if (!dialog.open) dialog.showModal();
  };

  const selectedControl = (picker) => {
    const selected = Array.from(picker.querySelectorAll('fieldset input:checked, select option:checked'));
    return (
      selected.find((control) => (control.dataset.optionName || '').toLowerCase().includes('siz')) ||
      selected[selected.length - 1] ||
      null
    );
  };

  const isPreorderSelection = (control) => {
    if (!control) return false;
    if (control.dataset.kittedPreorder === 'true') return true;
    const label = control.closest('label');
    return Boolean(label?.querySelector('.variant-option__strikethrough[data-kitted-preorder="true"]'));
  };

  const buttonTextNode = (button) =>
    Array.from(button?.childNodes || []).find(
      (node) => node.nodeType === Node.TEXT_NODE && node.textContent && node.textContent.trim().length > 0
    );

  const setButtonLabel = (button, label) => {
    const textNode = buttonTextNode(button);
    if (!textNode || textNode.textContent.trim() === label) return;
    textNode.textContent = `\n    ${label}\n    `;
  };

  const setPreorderProperty = (form, preorder) => {
    let input = form.querySelector('input[data-kitted-preorder-property]');

    if (preorder) {
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = PREORDER_PROPERTY;
        input.setAttribute('data-kitted-preorder-property', 'true');
        form.appendChild(input);
      }
      if (input.value !== PREORDER_VALUE) input.value = PREORDER_VALUE;
      if (input.getAttribute('value') !== PREORDER_VALUE) input.setAttribute('value', PREORDER_VALUE);
      input.disabled = false;
    } else if (input) {
      input.remove();
    }
  };

  const syncQuickAddNotice = (form, preorder) => {
    const dialog = form.closest('#quick-add-dialog dialog, dialog.quick-add-modal');
    if (!dialog) return;

    let notice = dialog.querySelector('[data-kitted-quick-preorder-notice]');
    if (!notice) {
      notice = document.createElement('div');
      notice.setAttribute('data-kitted-quick-preorder-notice', 'true');
      notice.setAttribute('role', 'status');
      notice.textContent = PREORDER_NOTICE;
      notice.style.cssText =
        'color:#b42318;font-weight:600;line-height:1.35;margin:12px 0;padding:10px 12px;border:1px solid currentColor;border-radius:6px;';

      const component = form.closest('product-form-component');
      if (component?.parentElement) {
        component.parentElement.insertBefore(notice, component);
      } else {
        form.parentElement?.insertBefore(notice, form);
      }
    }

    notice.hidden = !preorder;
  };

  const syncForm = (form, preorder) => {
    setPreorderProperty(form, preorder);
    syncQuickAddNotice(form, preorder);

    const component = form.closest('product-form-component');
    const button = component?.querySelector('button[ref="addToCartButton"]');
    if (!button) return;

    if (preorder) {
      setButtonLabel(button, PREORDER_LABEL);
    } else if (!button.disabled) {
      setButtonLabel(button, ADD_TO_CART_LABEL);
    }
  };

  const syncPicker = (picker) => {
    if (!(picker instanceof HTMLElement) || picker.closest('product-card')) return;

    const control = selectedControl(picker);
    const preorder = isPreorderSelection(control);
    const productId = picker.dataset.productId;
    const scope = picker.closest('.shopify-section, dialog') || document;

    let components = [];
    if (productId) {
      const escapedId = CSS.escape(productId);
      components = Array.from(scope.querySelectorAll(`product-form-component[data-product-id="${escapedId}"]`));
    }

    if (components.length === 0) {
      components = Array.from(scope.querySelectorAll('product-form-component'));
    }

    components.forEach((component) => {
      const form = component.querySelector('form[data-type="add-to-cart-form"], form[action*="/cart/add"]');
      if (form) syncForm(form, preorder);
    });
  };

  const syncAll = () => {
    document.querySelectorAll('variant-picker').forEach((picker) => {
      initializeRequiredSizePicker(picker);
      syncPicker(picker);
    });
  };

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const picker = target.closest('variant-picker');
    if (!picker) return;

    handleSizeSelection(picker, target);
    syncPicker(picker);
    setTimeout(() => syncPicker(picker), 80);
    setTimeout(() => syncPicker(picker), 450);
  });

  document.addEventListener('click', interceptPurchaseClick, true);

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const component = form.closest('product-form-component');
      if (!component) return;

      const scope = component.closest('.shopify-section, dialog') || document;
      const productId = component.dataset.productId;
      const picker = Array.from(scope.querySelectorAll('variant-picker')).find(
        (candidate) => !productId || candidate.dataset.productId === productId
      );
      if (picker) syncPicker(picker);
    },
    true
  );

  let syncQueued = false;
  const observer = new MutationObserver((mutations) => {
    const relevant = mutations.some((mutation) => {
      const target = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;
      if (target?.closest('variant-picker, product-form-component, #quick-add-dialog')) return true;

      return Array.from(mutation.addedNodes).some((node) => {
        if (!(node instanceof Element)) return false;
        return (
          node.matches('variant-picker, product-form-component, #quick-add-dialog') ||
          Boolean(node.querySelector('variant-picker, product-form-component'))
        );
      });
    });

    if (!relevant || syncQueued) return;
    syncQueued = true;
    queueMicrotask(() => {
      syncQueued = false;
      syncAll();
    });
  });

  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['checked', 'value', 'data-kitted-preorder'],
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAll, { once: true });
  } else {
    syncAll();
  }

  window.addEventListener('pageshow', syncAll);
})();
